import {
  featureListQuery,
  featureQuery,
  listQuery,
  route,
  searchParamsQuery,
  singleSourceQuery,
  sourceQuery,
} from '@/lib/routeGenerators';

/*
 * Route declaration guide:
 * - Use a string for static paths without parameters or query.
 * - Use route(path) for React Router path parameters such as :id.
 * - Use route(path, searchParamsQuery) when callers pass an already-built searchParams string.
 * - Use route(path, sourceQuery, true) for source, optional multipleSources, and urlPrefix.
 * - Use route(path, singleSourceQuery, true) when multipleSources must be ignored.
 * - Use route(path, listQuery(...), true) for source-filtered list pages with urlPrefix.
 * - Use featureQuery/featureListQuery only for routes that map featureType to source/type query.
 * - Pass true as the third argument only for routes that support urlPrefix.
 */
const routes = {
  urlDownloadImageArchive: route('/images/:id/archive'),
  urlPageAchievements: '/achievements',
  urlPageAchievementsDetails: route('/achievements/:id/view'),
  urlPageAchievementsEdit: route('/achievements/:id/edit'),
  urlPageBonusDetails: route('/bonuses/:bonusId/view'),
  urlPageBonusEdit: route('/bonuses/:bonusId/edit'),
  urlPageBonuses: '/bonuses',
  urlPageCategories: route('/categories', searchParamsQuery),
  urlPageCategoriesPriority: '/categories/priorities',
  urlPageCategoryCreate: '/categories/create',
  urlPageCategoryDetails: route('/categories/:categoryId/view'),
  urlPageCategoryEdit: route('/categories/:categoryId/edit'),
  urlPageCollectionCreate: route('/collections/create', sourceQuery, true),
  urlPageCollectionDetails: route('/collections/:collectionId/view', singleSourceQuery, true),
  urlPageCollectionEdit: route('/collections/:collectionId', sourceQuery, true),
  urlPageCollectionPriority: '/collections/priorities',
  urlPageCollections: route('/collections', listQuery({ includeType: true }), true),
  urlPageCreatePreset: '/presets/create',
  urlPageCreateTemplate: '/templates/create',
  urlPageDeeplinkCreate: '/deeplinks/create',
  urlPageDeeplinks: '/deeplinks',
  urlPageDictionaries: route('/dictionaries', listQuery(), true),
  urlPageDictionariesImport: route('/dictionaries/import', sourceQuery, true),
  urlPageDictionariesImportSuccess: route('/dictionaries/import/success', sourceQuery, true),
  urlPageDictionaryCreate: route('/dictionaries/create', sourceQuery, true),
  urlPageDictionaryEdit: route('/dictionaries/:id', sourceQuery, true),
  urlPageDynamicNews: '/news',
  urlPageDynamicNewsCreate: '/news/create',
  urlPageDynamicNewsDetails: route('/news/:newsId/view'),
  urlPageDynamicNewsEdit: route('/news/:newsId/edit'),
  urlPageDynamicNewsPriority: '/news/priorities',
  urlPageEditPreset: route('/presets/:presetId/edit'),
  urlPageEditTemplate: route('/templates/:templateId/edit'),
  urlPageEventCreate: '/events/create',
  urlPageEventDetails: route('/events/:eventId'),
  urlPageEventEdit: route('/events/:eventId/edit'),
  urlPageEvents: '/events',
  urlPageFeatureCreate: route('/:featureSource/create', featureQuery, true),
  urlPageFeatureDetails: route('/:featureSource/:featureId', featureQuery, true),
  urlPageFeaturePriorities: '/featured/priorities',
  urlPageFeatures: route('/:featureSource', featureListQuery, true),
  urlPageFeaturesEdit: route('/:featureSource/:featureId/edit', featureQuery, true),
  urlPageFeeds: route('/:feedSource'),
  urlPageFeedsPlanningDetails: route('/:feedSource/:date'),
  urlPageFeedsPlanningDetailsEdit: route('/:feedSource/:date/edit'),
  urlPageFilterCollectionCreate: '/filter-collections/create',
  urlPageFilterCollectionDetails: route('/filter-collections/:collectionId/view'),
  urlPageFilterCollectionEdit: route('/filter-collections/:collectionId/edit'),
  urlPageFilterCollections: '/filter-collections',
  urlPageFilterCreate: '/filters/create',
  urlPageFilterDetails: route('/filters/:filterId/view'),
  urlPageFilterEdit: route('/filters/:filterId/edit'),
  urlPageFilterGroupCreate: '/filter-groups/create',
  urlPageFilterGroupDetails: route('/filter-groups/:groupId/view'),
  urlPageFilterGroupEdit: route('/filter-groups/:groupId/edit'),
  urlPageFilterGroups: '/filter-groups',
  urlPageFilters: '/filters',
  urlPageImages: '/images',
  urlPageImagesReserveStatistics: '/images/reserve-statistics',
  urlPageImagesReserveStatisticsEdit: '/images/reserve-statistics/edit',
  urlPageImagesReserveStatisticsReference: '/images/reserve-statistics/reference',
  urlPageImagesReserveStatisticsReferenceEdit: '/images/reserve-statistics/reference/edit',
  urlPageImagesSearchSimilar: '/images/search-similar',
  urlPageLanguages: '/languages',
  urlPageLocalNotificationCreate: '/local-notifications/create',
  urlPageLocalNotificationDetails: route('/local-notifications/:notificationId'),
  urlPageLocalNotificationEdit: route('/local-notifications/:notificationId/edit'),
  urlPageLocalNotifications: '/local-notifications',
  urlPageLogin: '/login',
  urlPageNews: '/old-news',
  urlPageNewsDetails: route('/news/:newsId'),
  urlPageNewsPriority: '/old-news/priority',
  urlPagePopupCreate: route('/popups/create', singleSourceQuery, true),
  urlPagePopupDetails: route('/popups/:popupId', singleSourceQuery, true),
  urlPagePopupEdit: route('/popups/:popupId/edit', singleSourceQuery, true),
  urlPagePopups: route('/popups', listQuery({ searchBy: 'type', includeType: true }), true),
  urlPagePopupsPriority: '/popups/priorities',
  urlPagePresetDetails: route('/presets/:presetId/view'),
  urlPagePresetEdit: route('/presets/:presetId/edit'),
  urlPagePresets: route('/presets', searchParamsQuery),
  urlPageProfile: '/profile',
  urlPagePushIcons: '/push-icons',
  urlPagePushNotificationCreate: '/push-notifications/create',
  urlPagePushNotificationDetails: route('/push-notifications/:notificationId'),
  urlPagePushNotificationEdit: route('/push-notifications/:notificationId/edit'),
  urlPagePushNotificationScheduleClone: route('/push-notifications/schedule/:scheduleId/clone'),
  urlPagePushNotificationScheduleCreate: '/push-notifications/schedule/create',
  urlPagePushNotificationScheduleDetails: route('/push-notifications/schedule/:scheduleId'),
  urlPagePushNotificationScheduleEdit: route('/push-notifications/schedule/:scheduleId/edit'),
  urlPagePushNotifications: '/push-notifications',
  urlPagePushNotificationsSchedule: '/push-notifications/schedule',
  urlPageRBACLogDetails: route('/rbac/logs/:logId'),
  urlPageRBACLogs: '/rbac/logs',
  urlPageRBACPermissions: '/rbac/permissions',
  urlPageRBACRoleDetails: route('/rbac/roles/:roleId'),
  urlPageRBACRoles: '/rbac/roles',
  urlPageRBACUserDetails: route('/rbac/users/:userId'),
  urlPageRBACUsers: '/rbac/users',
  urlPageResetStaticContentLogs: '/static-content-reset-logs',
  urlPageStickerCreate: '/stickers/create',
  urlPageStickerDetails: route('/stickers/:stickerId'),
  urlPageStickerEdit: route('/stickers/:stickerId/edit'),
  urlPageStickers: '/stickers',
  urlPageStructures: '/structures',
  urlPageStructuresEdit: route('/structures/:id'),
  urlPageTemplateDetails: route('/templates/:templateId/view'),
  urlPageTemplates: route('/templates', searchParamsQuery),
  urlPageTemplatesVisualizations: '/templates/visualizations',
  urlPageTemplatesVisualizationsCreate: '/templates/visualizations/create',
  urlPageTemplatesVisualizationsDetails: route('/templates/visualizations/:visualizationId/view'),
  urlPageTemplatesVisualizationsEdit: route('/templates/visualizations/:visualizationId/edit'),
  urlPageTranslationDetails: route('/dictionaries/:id/translations', sourceQuery, true),
};

export default routes;
